import { useAppDispatch } from 'app/hooks';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useGetCompanyOwnersQuery } from 'features/company-owner/services';
import { useForm } from 'hooks/useForm';
import { FormEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAddDocumentMutation, useDeleteDocumentMutation } from 'services/documents';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { dataURLtoFile } from 'utils/file-helpers';
import { trimValues } from 'utils/object-helpers';
import { COMPANY_FIELDS_SCHEMA, COMPANY_INITIAL_FIELDS } from '../constants/company-fields';
import { useAddCompanyMutation } from '../services';
import { CompanyDto } from '../types/dtos';
import { useGetCitiesQuery } from 'features/city/service';

const useController = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [addCompany, { isLoading: isAddCompanyLoading }] = useAddCompanyMutation();
    const [addDocument, { isLoading: isAddDocumentLoading }] = useAddDocumentMutation();
    const [deleteDocument, { isLoading: isDeleteDocumentLoading }] = useDeleteDocumentMutation();
    const { data: companyOwners, isLoading: isGetCompanyOwnersLoading } = useGetCompanyOwnersQuery(FETCH_ALL_PARAMS);
    const { data: cities, isLoading: isGetCitiesLoading } = useGetCitiesQuery(FETCH_ALL_PARAMS);
    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({ defaultValues: COMPANY_INITIAL_FIELDS, validationSchema: COMPANY_FIELDS_SCHEMA });

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'company.COMPANY_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(
        (documentId?: string) => {
            const data = { ...formData };
            if (documentId) data.documentId = documentId;
            delete data.logo;
            return trimValues(data) as CompanyDto;
        },
        [formData],
    );

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                let documentId: string | undefined;
                try {
                    if (formData?.logo)
                        documentId = (await addDocument(dataURLtoFile(formData.logo)).unwrap()).documentId;

                    const payload = getPayload(documentId);
                    await addCompany(payload).unwrap();

                    handleSuccess();
                    navigate('../companies');
                } catch (error) {
                    documentId && (await deleteDocument(documentId).unwrap());
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [
            addCompany,
            addDocument,
            deleteDocument,
            formData?.logo,
            getPayload,
            handleErrors,
            handleSubmit,
            handleSuccess,
            navigate,
        ],
    );

    const onCancel = useCallback(() => {
        navigate('../companies');
    }, [navigate]);

    return {
        formData,
        cities,
        onSubmit,
        onCancel,
        onChange,
        isAddCompanyLoading: isAddCompanyLoading || isAddDocumentLoading || isDeleteDocumentLoading,
        isLoading: isGetCompanyOwnersLoading || isGetCitiesLoading,
        errors,
        companyOwners,
    };
};

export default useController;
